/**
 * TutorialOverlay - 首次進入遊戲時的基礎規則教學疊層
 * 5 個步驟：歡迎、手牌與召喚、戰場佈局、戰鬥規則、勝利條件
 * 可關閉，且記住已讀狀態（localStorage）
 */

import { useState, useCallback } from 'react';

const TUTORIAL_KEY = 'xinglingwang_tutorial_completed';

interface TutorialStep {
  title: string;
  icon: string;
  content: string[];
}

const TUTORIAL_STEPS: TutorialStep[] = [
  {
    title: '歡迎來到星靈王！',
    icon: '⭐',
    content: [
      '星靈王是一款回合制卡牌對戰遊戲。',
      '你將使用 20 張卡牌組成的牌組與對手對戰。',
      '以下是遊戲的基本規則介紹。',
    ],
  },
  {
    title: '手牌與召喚',
    icon: '🃏',
    content: [
      '每回合開始時，你會從牌庫抽 2 張卡牌。',
      '手牌上限為 10 張，超過的牌會自動送入墓地。',
      '在召喚階段，你可以將手牌中的單位放到戰場上。',
      '選擇一張手牌，然後點擊戰場上的空位放置。',
    ],
  },
  {
    title: '戰場佈局',
    icon: '🏟️',
    content: [
      '戰場分為前排和後排，每排有 5 個格子。',
      '你的單位放在下方（前排+後排）。',
      '對手的單位在上方。',
      '前排單位會直接面對敵方的前排攻擊。',
    ],
  },
  {
    title: '戰鬥規則',
    icon: '⚔️',
    content: [
      '在戰術階段，你可以選擇單位宣告攻擊。',
      '攻擊力（ATK）較高的一方會摧毀對方的單位。',
      '同一戰線（1-5 號位）的單位會互相對決。',
      '被摧毀的單位會進入墓地。',
    ],
  },
  {
    title: '勝利條件',
    icon: '🏆',
    content: [
      '當任一方牌庫耗盡時，進入 5 回合倒數。',
      '倒數結束後，計算雙方戰場上單位的總分數。',
      '分數較高的一方獲勝！',
      '善用卡牌效果和戰略部署來贏得勝利吧！',
    ],
  },
];

/**
 * 檢查教學是否已完成
 */
export function isTutorialCompleted(): boolean {
  try {
    return localStorage.getItem(TUTORIAL_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * 標記教學為已完成
 */
function markTutorialCompleted(): void {
  try {
    localStorage.setItem(TUTORIAL_KEY, 'true');
  } catch {
    // localStorage 不可用，忽略
  }
}

export function TutorialOverlay() {
  const [currentStep, setCurrentStep] = useState(0);
  const [visible, setVisible] = useState(() => !isTutorialCompleted());

  const handleNext = useCallback(() => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      // 最後一步，關閉並記住已讀
      markTutorialCompleted();
      setVisible(false);
    }
  }, [currentStep]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  }, [currentStep]);

  const handleSkip = useCallback(() => {
    markTutorialCompleted();
    setVisible(false);
  }, []);

  if (!visible) return null;

  const step = TUTORIAL_STEPS[currentStep];
  const isLast = currentStep === TUTORIAL_STEPS.length - 1;
  const isFirst = currentStep === 0;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* 標題 */}
        <div style={styles.header}>
          <span style={styles.icon}>{step.icon}</span>
          <h2 style={styles.title}>{step.title}</h2>
        </div>

        {/* 內容 */}
        <div style={styles.content}>
          {step.content.map((line, i) => (
            <p key={i} style={styles.paragraph}>
              {line}
            </p>
          ))}
        </div>

        {/* 步驟指示器 */}
        <div style={styles.dots}>
          {TUTORIAL_STEPS.map((_, i) => (
            <span
              key={i}
              style={{
                ...styles.dot,
                backgroundColor: i === currentStep ? '#ffd700' : '#444466',
              }}
            />
          ))}
        </div>

        {/* 按鈕 */}
        <div style={styles.buttons}>
          <button style={styles.skipButton} onClick={handleSkip}>
            跳過教學
          </button>
          <div style={styles.navButtons}>
            {!isFirst && (
              <button style={styles.prevButton} onClick={handlePrev}>
                ← 上一步
              </button>
            )}
            <button style={styles.nextButton} onClick={handleNext}>
              {isLast ? '開始遊戲！' : '下一步 →'}
            </button>
          </div>
        </div>

        {/* 步驟文字 */}
        <div style={styles.stepText}>
          {String(currentStep + 1)} / {String(TUTORIAL_STEPS.length)}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
    fontFamily: 'Arial, sans-serif',
  },
  modal: {
    backgroundColor: '#1a1a2e',
    borderRadius: '16px',
    border: '2px solid #ffd700',
    padding: '40px',
    maxWidth: '520px',
    width: '90%',
    boxShadow: '0 8px 32px rgba(255, 215, 0, 0.2)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
  },
  icon: {
    fontSize: '36px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#ffd700',
    margin: 0,
  },
  content: {
    marginBottom: '24px',
  },
  paragraph: {
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#ccccee',
    margin: '0 0 8px 0',
  },
  dots: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '24px',
  },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    transition: 'background-color 0.3s',
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  skipButton: {
    padding: '8px 16px',
    fontSize: '13px',
    backgroundColor: 'transparent',
    color: '#666688',
    border: '1px solid #444466',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  navButtons: {
    display: 'flex',
    gap: '8px',
  },
  prevButton: {
    padding: '10px 20px',
    fontSize: '14px',
    backgroundColor: '#3a3a5a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  nextButton: {
    padding: '10px 24px',
    fontSize: '14px',
    backgroundColor: '#4a3a1a',
    color: '#ffd700',
    border: '2px solid #ffd700',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  stepText: {
    textAlign: 'center',
    fontSize: '12px',
    color: '#666688',
    marginTop: '16px',
  },
};
